import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, map, Observable, tap } from 'rxjs';
import { BackendService, PermissionService } from '@ipg/ngx-frontend-toolset';
import { DialogService } from '../../../components/dialog/dialog.service';

interface FolderWarningResponse {
  hasEmptyBlocks: boolean;
}

/**
 * Service that checks whether any SCM folder (tag) blocks for a client
 * have 0 matching published interactions and shows a warning dialog.
 *
 * Designed for post-action use: call after deleting, tag-changing, or
 * publishing an interaction so the user is informed if an SCM block
 * will no longer be visible on the robot.
 */
@Injectable({
  providedIn: 'root',
})
export class ScmFolderWarningService {
  private readonly _backend = inject(BackendService);
  private readonly _dialog = inject(DialogService);
  private readonly _permissionService = inject(PermissionService);

  /**
   * Check if any SCM folder blocks for the given client now have 0
   * matching interactions, and show a warning if so.
   *
   * @param clientId The client to check.
   * @param tagIds The interaction's tag IDs (used server-side to skip
   *               blocks whose tags don't overlap).
   * @returns Observable that completes after the check (and optional dialog).
   *          Errors are silently swallowed so the main flow is never interrupted.
   *          Skipped without `scm:read`: the check reads the signaling, which a
   *          role may lack while still deleting or publishing interactions, and
   *          a refusal there would greet a finished action with a notice.
   */
  checkAndWarn(clientId: string, tagIds: string[]): Observable<void> {
    if (!this._permissionService.hasPermission('scm:read')) {
      return EMPTY;
    }

    return this._backend
      .get<FolderWarningResponse>(
        `/clients/${clientId}/signaling/folder-warnings`,
        {
          params: { tagIds },
        }
      )
      .pipe(
        tap((response) => {
          if (response.hasEmptyBlocks) {
            this._showWarningDialog();
          }
        }),
        map(() => void 0),
        // Never let check failures break the caller's flow
        catchError(() => EMPTY)
      );
  }

  private _showWarningDialog(): void {
    this._dialog.alert({
      title: 'Signaleringsblok zonder interacties',
      message:
        'Er zijn signaleringsblokken die geen overeenkomende interacties meer hebben ' +
        'en daardoor niet zichtbaar zullen zijn op de robot. ' +
        'Controleer de signaleringsinstellingen van deze client.',
      confirmText: 'Begrepen',
    });
  }
}
